import React, { useContext, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Scope } from '@/contexts/ScopeContext';
import useDebugMode from '@/hooks/useDebugMode';
import { BENCHMARK_EXPORT, downloadExport, exportRange } from './export';

/** Internal-only entry point; this package is independent of analytics tables and filters. */
export default function BenchmarkExportButton({ organizationId }: { organizationId?: number | string }) {
  const { canBypassPermissions } = useDebugMode();
  const scope = useContext(Scope);
  const id = Number(organizationId ?? scope?.hospital?.id);
  return canBypassPermissions && Number.isSafeInteger(id) && id > 0 ? <ExportDialog organizationId={id} /> : null;
}

function ExportDialog({ organizationId }: { organizationId: number }) {
  const client = useApolloClient();
  const now = new Date();
  const previousMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(previousMonth);
  const [through, setThrough] = useState(previousMonth);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setError(null);
    try {
      const range = exportRange(from, through);
      const { data } = await client.query({ query: BENCHMARK_EXPORT,
        variables: { organizationId, ...range }, fetchPolicy: 'no-cache' });
      downloadExport(data.benchmarkExport.payload, data.benchmarkExport.fileName);
      setOpen(false);
    } catch (e) { setError(e instanceof Error ? e.message : 'Benchmark export failed.'); }
    finally { setBusy(false); }
  };
  return <>
    <Button color="inherit" onClick={() => { setError(null); setOpen(true); }}>Benchmark export</Button>
    <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Internal benchmark export</DialogTitle>
      <DialogContent>
        <Typography variant="body2" paragraph>Choose any range of complete calendar months for the internal benchmark tool. Includes the OR case cohort and utilization for all rooms.</Typography>
        <TextField type="month" label="From month" value={from} onChange={e => setFrom(e.target.value)}
          disabled={busy} fullWidth margin="normal" InputLabelProps={{ shrink: true }} />
        <TextField type="month" label="Through month" value={through} onChange={e => setThrough(e.target.value)}
          disabled={busy} fullWidth margin="normal" InputLabelProps={{ shrink: true }} />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
        <Button color="primary" variant="contained" disabled={busy} onClick={run}>
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Download benchmark JSON'}
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}
